/**
 * AI Readiness Lead Magnet
 *
 * Shared intake and validation helpers for the public
 * AI Readiness lead magnet flow.
 *
 * Primary responsibilities:
 * - Normalize public lead-magnet identity fields
 * - Normalize business email and website inputs
 * - Keep shared domain rules in one place
 *
 * Dependencies:
 * - Quick AI readiness category config
 * - Quick AI normalization utilities
 */

import { DISPOSABLE_EMAILS } from '@/lib/disposable-emails'
import {
  normalizeWebsiteUrl,
  toTitleCase
} from '@/lib/quick-ai-readiness/utils'

export const AI_READINESS_LEAD_MAGNET_SOURCES = [
  'enterprise_pricing',
  'enterprise_page',
  'marketing_homepage',
  'marketing_footer',
  'ai_readiness_page'
] as const

export type AiReadinessLeadMagnetSource =
  (typeof AI_READINESS_LEAD_MAGNET_SOURCES)[number]

export const AI_READINESS_CONSUMER_EMAIL_DOMAINS = new Set([
  'aol.com',
  'duck.com',
  'gmail.com',
  'googlemail.com',
  'gmx.com',
  'hey.com',
  'hotmail.com',
  'icloud.com',
  'live.com',
  'mac.com',
  'mail.com',
  'me.com',
  'msn.com',
  'outlook.com',
  'pm.me',
  'proton.me',
  'protonmail.com',
  'qq.com',
  'yahoo.ca',
  'yahoo.com',
  'yandex.com',
  ...DISPOSABLE_EMAILS
])

// --------------

export function normalizeLeadMagnetEmail(value: string): string {
  return value.trim().toLowerCase()
}

// --------------

export function extractLeadMagnetEmailDomain(email: string): string {
  const normalizedEmail = normalizeLeadMagnetEmail(email)
  const [, rawDomain] = normalizedEmail.split('@')

  if (!rawDomain) {
    throw new Error('Enter a valid business email address.')
  }

  return rawDomain.trim().toLowerCase()
}

// --------------

export function isRestrictedLeadMagnetEmailDomain(domain: string): boolean {
  return AI_READINESS_CONSUMER_EMAIL_DOMAINS.has(domain.trim().toLowerCase())
}

// --------------

export function assertBusinessEmail(email: string): {
  email: string
  domain: string
} {
  const normalizedEmail = normalizeLeadMagnetEmail(email)
  const domain = extractLeadMagnetEmailDomain(normalizedEmail)

  if (isRestrictedLeadMagnetEmailDomain(domain)) {
    throw new Error('Use your company email so we can evaluate the correct business domain.')
  }

  return {
    email: normalizedEmail,
    domain
  }
}

// --------------

export function buildLeadMagnetWebsite(domain: string): string {
  return normalizeWebsiteUrl(`https://${domain}`)
}

// --------------

export function normalizeLeadMagnetWebsiteOverride(value?: string | null): string | null {
  if (!value || !value.trim()) {
    return null
  }

  return normalizeWebsiteUrl(value)
}

// --------------

export function buildLeadMagnetBusinessName(domain: string): string {
  const [firstLabel] = domain
    .split('.')
    .filter(Boolean)
    .filter((label) => !['www', 'app', 'go', 'home', 'portal'].includes(label.toLowerCase()))

  if (!firstLabel) {
    return toTitleCase(domain.replace(/\./g, ' '))
  }

  const cleanedLabel = firstLabel.replace(/\d+/g, '').replace(/[-_]+/g, ' ').trim()
  return toTitleCase(cleanedLabel || firstLabel)
}

// --------------

export function normalizeLeadMagnetFullName(value: string): string {
  return value
    .trim()
    .replace(/\s+/g, ' ')
}

// --------------

export function splitLeadMagnetFullName(fullName: string): {
  firstName: string
  lastName: string
} {
  const normalizedFullName = normalizeLeadMagnetFullName(fullName)
  const segments = normalizedFullName.split(' ').filter(Boolean)
  const firstName = segments[0] || normalizedFullName
  const lastName = segments.slice(1).join(' ') || firstName

  return {
    firstName,
    lastName
  }
}

// --------------

export function normalizeLeadMagnetJobTitle(value: string): string {
  return value
    .trim()
    .replace(/\s+/g, ' ')
}
